import {
  CaretDown,
  Check,
  CircleNotch,
  Clock,
  Info,
  Sparkle,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";
import { getAssessmentSnapshot } from "../services/api";

type ReviewItem = { label: string; help: string };
type ReviewGroup = { id: string; title: string; items: ReviewItem[] };

const reviewGroups: ReviewGroup[] = [
  {
    id: "opt-in",
    title: "Opt-in form review",
    items: [
      { label: "Business name clearly displayed", help: "The legal business name or selected DBA should appear beside the opt-in experience." },
      { label: "Checkbox for explicit consent", help: "SMS consent should use a clear, unchecked checkbox or another affirmative action." },
      { label: "Privacy Policy and Terms links", help: "Both policy links should be visible where consent is collected." },
      { label: "Opt-out instructions", help: "The disclosure should explain that recipients can reply STOP to opt out." },
      { label: "Message frequency disclosure", help: "Tell customers how often messages may be sent, even when frequency varies." },
      { label: "Consent is optional", help: "The checkbox cannot be mandatory or presented as a condition of purchase." },
      { label: "Message type disclosure", help: "Describe the kinds of texts customers are agreeing to receive." },
      { label: "Message and data rates disclosure", help: "State that message and data rates may apply." },
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy review",
    items: [
      { label: "Information collected", help: "The policy should describe the personal and contact information the business collects." },
      { label: "How customer data is used", help: "Explain why information is collected and how it supports the service." },
      { label: "SMS opt-in details", help: "Explain how customers give permission to receive text messages." },
      { label: "Cookie and tracking practices", help: "Disclose applicable website analytics, cookies, and tracking tools." },
      { label: "Data security and handling", help: "Describe reasonable safeguards and how information is managed." },
      { label: "Customer privacy rights", help: "Explain how customers can access, correct, or request deletion of their information." },
      { label: "Mobile information sharing statement", help: "State that mobile opt-in data and consent are not sold or shared for third-party marketing." },
    ],
  },
  {
    id: "terms",
    title: "Terms of Service review",
    items: [
      { label: "Description of SMS use cases", help: "Describe the Low Volume Mixed messages customers may receive." },
      { label: "Opt-out instructions", help: "Explain how recipients can stop messages by replying STOP." },
      { label: "Customer support contact", help: "Provide a support channel and explain that HELP returns assistance." },
      { label: "Message and data rate disclosure", help: "State that carrier message and data rates may apply." },
      { label: "Carrier liability disclaimer", help: "Clarify that carriers are not liable for delayed or undelivered messages." },
      { label: "Age requirement", help: "State the minimum age required to use the service or participate in the program." },
      { label: "Link to Privacy Policy", help: "The Terms should provide a working link to the current Privacy Policy." },
    ],
  },
];

const totalChecks = reviewGroups.reduce((total, group) => total + group.items.length, 0);
const mockMode =
  import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" ||
  (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "false");

export function ProcessingPage() {
  const [active, setActive] = useState(0);
  const [openGroups, setOpenGroups] = useState(() => new Set(reviewGroups.map((group) => group.id)));
  const [openHelp, setOpenHelp] = useState<string | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { credentials } = useAssessmentSession();
  const complete = active >= totalChecks;
  const completedCount = Math.min(active, totalChecks);
  const progress = Math.round((completedCount / totalChecks) * 100);

  const groupOffsets = useMemo(() => {
    let offset = 0;
    return reviewGroups.map((group) => {
      const value = offset;
      offset += group.items.length;
      return value;
    });
  }, []);

  useEffect(() => {
    if (mockMode) {
      const timer = window.setInterval(
        () => setActive((value) => Math.min(value + 1, totalChecks)),
        420,
      );
      return () => window.clearInterval(timer);
    }
    if (!credentials) return;
    const poll = window.setInterval(
      () =>
        void getAssessmentSnapshot(credentials)
          .then((snapshot) => {
            if (snapshot.result) {
              window.clearInterval(poll);
              navigate(`/assessment/${id}/results`);
            } else setActive((value) => Math.min(value + 1, totalChecks - 1));
          })
          .catch(() => undefined),
      3000,
    );
    return () => window.clearInterval(poll);
  }, [credentials, id, navigate]);

  function toggleGroup(groupId: string) {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  return (
    <main className="processing-review min-h-[calc(100dvh-92px)] px-5 py-10 sm:px-8 sm:py-14">
      <div className="mx-auto w-full max-w-5xl">
        <header className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <p className="eyebrow">Compliance review</p>
            <h1 className="mt-3 font-display text-3xl font-semibold tracking-[-.04em] sm:text-5xl">
              Checking the details reviewers see.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-[#8D9591]">
              Your opt-in experience and policy pages are being checked together for a Low Volume Mixed campaign.
            </p>
          </div>
          <div className="review-total">
            <strong>{completedCount.toString().padStart(2, "0")}</strong>
            <span>of {totalChecks} checks</span>
          </div>
        </header>

        <div className="review-progress mt-8" aria-label={`${progress}% complete`}>
          <span style={{ width: `${progress}%` }} />
        </div>

        <section className="mt-5 grid gap-3" aria-label="Compliance review progress">
          {reviewGroups.map((group, groupIndex) => {
            const offset = groupOffsets[groupIndex] ?? 0;
            const groupCompleted = Math.max(0, Math.min(active - offset, group.items.length));
            const groupActive = active >= offset && active < offset + group.items.length;
            const groupDone = groupCompleted === group.items.length;
            const isOpen = openGroups.has(group.id);
            return (
              <article key={group.id} className={`review-group ${groupActive ? "is-active" : ""} ${groupDone ? "is-complete" : ""}`}>
                <button
                  type="button"
                  className="review-group-header"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                >
                  <span className="review-group-state">
                    {groupDone ? <Check size={17} weight="bold" /> : groupActive ? <CircleNotch size={18} className="animate-spin" /> : <Clock size={17} />}
                  </span>
                  <span className="flex-1 text-left">
                    <strong>{group.title}</strong>
                    <small>{groupCompleted} of {group.items.length} checked</small>
                  </span>
                  <span className="review-status">{groupDone ? "Complete" : groupActive ? "Reviewing" : "Queued"}</span>
                  <CaretDown className={`review-caret ${isOpen ? "rotate-180" : ""}`} size={18} />
                </button>
                {isOpen && (
                  <div className="review-items">
                    {group.items.map((item, itemIndex) => {
                      const absoluteIndex = offset + itemIndex;
                      const itemDone = absoluteIndex < active;
                      const itemActive = absoluteIndex === active && !complete;
                      const helpId = `${group.id}-${itemIndex}`;
                      return (
                        <div key={item.label} className={`review-item-wrap ${openHelp === helpId ? "is-open" : ""}`}>
                          <div className={`review-item ${itemActive ? "is-active" : ""}`}>
                            <span className={`review-item-state ${itemDone ? "is-complete" : ""}`}>
                              {itemDone ? <Check size={14} weight="bold" /> : itemActive ? <CircleNotch size={15} className="animate-spin" /> : <Clock size={15} />}
                            </span>
                            <span className="flex-1">{item.label}</span>
                            <button
                              type="button"
                              className="review-help"
                              aria-label={`Explain ${item.label}`}
                              aria-expanded={openHelp === helpId}
                              aria-controls={`review-help-${helpId}`}
                              onClick={() => setOpenHelp((current) => current === helpId ? null : helpId)}
                            >
                              <Info size={15} />
                            </button>
                          </div>
                          {openHelp === helpId && (
                            <p id={`review-help-${helpId}`} className="review-help-text">
                              {item.help}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </section>

        <footer className="review-footer mt-5">
          <div className="flex items-start gap-3">
            <Sparkle size={19} className="mt-0.5 shrink-0 text-brand" weight="fill" />
            <p className="text-xs leading-5 text-[#737C77]">
              {complete
                ? "The review is complete. Your readiness findings are ready."
                : "You can safely leave this page. Production reviews continue through the assessment Worker and update this temporary session when complete."}
            </p>
          </div>
          <Button
            onClick={() => navigate(`/assessment/${id}/results`)}
            disabled={!complete}
          >
            View results
          </Button>
        </footer>
      </div>
    </main>
  );
}
